// MediaPipe Pose Landmarker, self-hosted. Tries the GPU delegate first and
// falls back to CPU (Wasm SIMD) if WebGL isn't usable.

import type { FilesetResolver, PoseLandmarker } from '@mediapipe/tasks-vision';

type WasmFileset = Awaited<ReturnType<typeof FilesetResolver.forVisionTasks>>;
import { packLandmarks } from '../core/pose';
import type { ModelVariant } from './db';

export interface Detector {
  readonly variant: ModelVariant;
  readonly delegate: 'GPU' | 'CPU';
  /** Detects the most prominent person; null when nobody is found. */
  detect(source: TexImageSource, timestampMs: number): Float32Array | null;
  close(): void;
}

const assetUrl = (path: string) => new URL(path, document.baseURI).href;

/** Pinned copy on Google's model CDN, for hosts that can't serve a 30 MB file. */
const HEAVY_REMOTE = 'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_heavy/float16/1/pose_landmarker_heavy.task';
const modelUrl = (variant: ModelVariant) =>
  variant === 'heavy' && import.meta.env.VITE_HEAVY_MODEL_REMOTE ? HEAVY_REMOTE : assetUrl(`models/pose_landmarker_${variant}.task`);

// Loaded on demand: the demo and the review screens never need it.
const vision = () => import('@mediapipe/tasks-vision');
let filesetPromise: Promise<WasmFileset> | null = null;
const fileset = () => (filesetPromise ??= vision().then((v) => v.FilesetResolver.forVisionTasks(assetUrl('mediapipe'))));

export async function createDetector(variant: ModelVariant, prefer: 'GPU' | 'CPU'): Promise<Detector> {
  const order: ('GPU' | 'CPU')[] = prefer === 'GPU' ? ['GPU', 'CPU'] : ['CPU'];
  let lastError: unknown;
  for (const delegate of order) {
    try {
      const { PoseLandmarker } = await vision();
      const landmarker = await PoseLandmarker.createFromOptions(await fileset(), {
        baseOptions: { modelAssetPath: modelUrl(variant), delegate },
        runningMode: 'VIDEO',
        numPoses: 1,
        minPoseDetectionConfidence: 0.5,
        minPosePresenceConfidence: 0.5,
        minTrackingConfidence: 0.5,
        outputSegmentationMasks: false,
      });
      return wrap(landmarker, variant, delegate);
    } catch (e) {
      lastError = e;
      console.warn(`Pose landmarker (${delegate}) failed to start`, e);
    }
  }
  throw lastError instanceof Error ? lastError : new Error('Could not start the pose model.');
}

function wrap(landmarker: PoseLandmarker, variant: ModelVariant, delegate: 'GPU' | 'CPU'): Detector {
  let lastTs = -1;
  return {
    variant,
    delegate,
    detect(source, timestampMs) {
      // MediaPipe requires strictly increasing timestamps.
      const ts = Math.max(Math.round(timestampMs), lastTs + 1);
      lastTs = ts;
      const result = landmarker.detectForVideo(source, ts);
      const pose = result.landmarks[0];
      return pose && pose.length >= 33 ? packLandmarks(pose) : null;
    },
    close() {
      landmarker.close();
    },
  };
}

// One shared live detector; recreated when the model or delegate changes.
let shared: { key: string; detector: Promise<Detector> } | null = null;

export function sharedDetector(variant: ModelVariant, prefer: 'GPU' | 'CPU'): Promise<Detector> {
  const key = `${variant}/${prefer}`;
  if (shared?.key === key) return shared.detector;
  const previous = shared?.detector;
  const detector = createDetector(variant, prefer);
  shared = { key, detector };
  detector.catch(() => {
    if (shared?.key === key) shared = null;
  });
  previous?.then((d) => d.close()).catch(() => {});
  return detector;
}
